import { useTranslation } from 'react-i18next'
import TextPropertyEditor from './property-editors/TextPropertyEditor'
import BooleanPropertyEditor from './property-editors/BooleanPropertyEditor'
import IntegerPropertyEditor from './property-editors/IntegerPropertyEditor'
import { HelpHint } from '@/components/common/help/HelpHint'
import { HelpText } from '@/components/common/help/HelpText'
import { LocalizationEntityType } from '@/api/model/localizationEntityType'
import { NestedTranslateButton } from '@/components/common/translations/NestedTranslateButton'
import type { SubtypeState, SubtypeTranslations } from '@/lib/action-modules/registry/types'
import { PropertyTypes, type PropertyType, type SubtypeProperty } from '@/lib/action-modules/registry/subtypes/types'
import { subtypeDefaultProperties } from '@/lib/action-modules/registry'
import { subtypePropertyName } from '../subtypeTranslations'

interface Props {
  selectedSubtype?: SubtypeState
  subtypes: SubtypeState[]
  translations: SubtypeTranslations
  onChange: (oldSubtype: SubtypeState, newSubtype: SubtypeState) => void
  disabled?: boolean
}

export default function SubtypePropertyEditor({ selectedSubtype, subtypes, translations, onChange, disabled = false }: Props) {
  const { t } = useTranslation('action-modules-wizard')
  const subtype = selectedSubtype
  if (!subtype) return null

  const entityId = translations.entityId
  const subtypeId = subtype.subtype.id

  const renderProperty = (p: SubtypeProperty) => {
    if (p.hidden) return null

    const editor = (
      <PropertyEditor
        property={p}
        subtype={subtype}
        allSubtypes={subtypes}
        translations={translations}
        onChanged={next => onChange(subtype, next)}
      />
    )

    // The translation row is keyed by the subtype id, so translating only starts once the
    // subtype is persisted — same rule as the condition editors.
    const property = p.translatable && entityId && subtypeId != null
      ? subtypePropertyName(subtypeId, p.name)
      : null

    return (
      <div key={p.name} className="flex flex-col gap-1">
        <div className="flex items-center gap-1.5">
          <h5 className="text-sm font-medium">{t(p.displayNameKey)}</h5>
          {p.helpKey && <HelpHint helpKey={p.helpKey} />}
        </div>
        {property && entityId ? (
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">{editor}</div>
            <NestedTranslateButton
              entityType={LocalizationEntityType.ActionModule}
              entityId={entityId}
              property={property}
              edited={translations.get(property)}
              onChange={v => translations.set(property, v)}
              dialogTitle={t('tabSubtypes.translateTitle', { field: t(p.displayNameKey) })}
              plannedLanguages={translations.getPlanned(property)}
              onPlannedChange={v => translations.setPlanned(property, v)}
              onLoaded={v => translations.setServerLoaded(property, v)}
            />
          </div>
        ) : (
          editor
        )}
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-2">
      {/* What this building block does — the same text the toolbox chip offers as a popover,
          shown here because the user is now configuring it. */}
      {subtype.definition.helpKey && <HelpText helpKey={subtype.definition.helpKey} />}
      {/* A native <fieldset disabled> disables every descendant form control (inputs, selects,
          buttons, textareas) in one place, covering both the built-in and custom property
          editors without threading `disabled` through each one. min-w-0 keeps flex children
          shrinkable inside the fieldset. */}
      <fieldset disabled={disabled} className="flex min-w-0 flex-col gap-3 border-0 p-0 disabled:opacity-60">
        {subtypeDefaultProperties.map(renderProperty)}
        {subtype.definition.editableProperties.map(renderProperty)}
      </fieldset>
    </div>
  )
}

function PropertyEditor(props: {
  property: SubtypeProperty
  subtype: SubtypeState
  allSubtypes: SubtypeState[]
  translations: SubtypeTranslations
  onChanged: (newState: SubtypeState) => void
}) {
  const { t } = useTranslation('action-modules-wizard')
  const value = props.subtype.properties[props.property.name]

  const onChanged = (newValue: PropertyType | undefined) => {
    const properties = { ...props.subtype.properties }
    if (newValue === undefined || newValue === null) delete properties[props.property.name]
    else properties[props.property.name] = newValue
    props.onChanged({ ...props.subtype, properties })
  }

  const editorProps = {
    value,
    onChanged,
    property: props.property,
    subtype: props.subtype,
    allSubtypes: props.allSubtypes,
    translations: props.translations,
  }

  if (props.property.editor) {
    return props.property.editor(editorProps)
  }

  switch (props.property.propertyType) {
    case PropertyTypes.text:
      return <TextPropertyEditor {...editorProps} />
    case PropertyTypes.boolean:
      return <BooleanPropertyEditor {...editorProps} />
    case PropertyTypes.integer:
      return <IntegerPropertyEditor {...editorProps} />
    default:
      return <p className="text-sm text-muted-foreground">{t('tabSubtypes.notEditableProperty')}</p>
  }
}
